import { inject } from '@angular/core';
import type { Route, Routes } from '@angular/router';

import { authGuard } from '@app/auth/auth.guard';
import { projectPermissionLoader } from '@app/auth/project.guard';
import { redirectToHomeGuard } from '@app/auth/redirect-to-home.guard';
import {
    MODULE_DISPLAY_NAME,
    MODULE_ROUTE_PATH,
    MODULES,
    PAGE_ROUTE_PATH,
} from '@app/shared/common/shared.constant';
import { BreadcrumbService } from '@app/shared/routing/breadcrumb.service';
import type { ModuleRouteData } from '@app/shared/routing/router-factory';
import { handleChunkNotFound } from '@app/theme/chunk-not-found-dialog/chunk-not-found';
import { ChunkNotFoundDialogService } from '@app/theme/chunk-not-found-dialog/chunk-not-found-dialog.service';

interface ModuleRoute extends Route {
    path: MODULE_ROUTE_PATH;
    data: ModuleRouteData & { breadcrumb: string };
    canMatch: [typeof authGuard];
    loadChildren: Route['loadChildren'];
}

const projectModuleRoutes: ModuleRoute[] = [
    {
        path: MODULE_ROUTE_PATH.ASSETS,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/assets/assets.routes')
                .then(m => m.assetsRoutes)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.ASSETS],
            moduleName: MODULES.ASSETS,
        },
        canMatch: [authGuard],
    },
    {
        path: MODULE_ROUTE_PATH.TESTING,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/testing/testing.routes')
                .then(m => m.testingRoutes)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.TESTING],
            moduleName: MODULES.TESTING,
        },
        canMatch: [authGuard],
    },
    {
        path: MODULE_ROUTE_PATH.MONITOR,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/monitor/monitor.routes')
                .then(m => m.monitorRoutes)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.MONITOR],
            moduleName: MODULES.MONITOR,
        },
        canMatch: [authGuard],
    },
    {
        path: MODULE_ROUTE_PATH.MANAGEMENT,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/management/management.routes')
                .then(m => m.managementRoutes)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.MANAGEMENT],
            moduleName: MODULES.MANAGEMENT,
        },
        canMatch: [authGuard],
    },
];

const extraRoutes: Routes = [
    {
        path: MODULE_ROUTE_PATH.HOME,
        loadComponent: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/home-page/home-page.component')
                .then(m => m.HomePageComponent)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: 'Home',
        },
        canMatch: [authGuard],
    },
    {
        path: 'ai-chat',
        loadComponent: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/shared/ai-chat/ai-chat/ai-chat.component')
                .then(m => m.AIChatComponent)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        canActivate: [authGuard],
        data: {
            title: 'CORRA Chat',
            breadcrumb: 'CORRA Chat',
        },
    },
    {
        path: 'unauthorised',
        loadComponent: () =>
            import('@app/shared/page-state/forbidden/forbidden.component').then(
                m => m.ForbiddenComponent,
            ),
        pathMatch: 'full',
        data: {
            title: 'Forbidden',
        },
    },
    {
        path: 'reload',
        loadComponent: () =>
            import('@app/theme/splash/splash.component').then(m => m.SplashComponent),
        pathMatch: 'full',
        data: {
            title: 'Reloading...',
        },
    },
    {
        path: 'not-found',
        loadComponent: () =>
            import('@app/shared/page-state/not-found/not-found.component').then(
                m => m.NotFoundComponent,
            ),
        pathMatch: 'full',
        data: {
            title: 'Not Found',
        },
    },
];

export const themeRoutes: Routes = [
    {
        path: MODULE_ROUTE_PATH.SETTING,
        outlet: 'settings',
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/settings/settings-routes')
                .then(m => m.settingsRoutes)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        canMatch: [authGuard],
    },
    {
        path: '',
        loadComponent: () => import('@app/theme/theme.component').then(m => m.ThemeComponent),
        canMatch: [authGuard],
        children: [
            { path: '', redirectTo: MODULE_ROUTE_PATH.HOME, pathMatch: 'full' },
            ...projectModuleRoutes,
            ...extraRoutes,
        ],
    },
    {
        path: ':projectSlug',
        loadComponent: () => import('@app/theme/theme.component').then(m => m.ThemeComponent),
        canMatch: [authGuard, projectPermissionLoader],
        resolve: { customBreadcrumbs: BreadcrumbService },
        children: [
            {
                path: '',
                loadComponent: () => {
                    const dialogService = inject(ChunkNotFoundDialogService);
                    return import(
                        '@app/assets/projects/project-home-page/project-home-page.component'
                    )
                        .then(m => m.ProjectHomePageComponent)
                        .catch(err => handleChunkNotFound(err, dialogService));
                },
                canMatch: [authGuard],
                pathMatch: 'full',
            },
            ...projectModuleRoutes,
            {
                path: PAGE_ROUTE_PATH.PROJECT_SETTINGS,
                loadChildren: () => {
                    const dialogService = inject(ChunkNotFoundDialogService);
                    return import('@app/assets/projects/project-settings/project-settings.route')
                        .then(m => m.projectSettingsRoutes)
                        .catch(err => handleChunkNotFound(err, dialogService));
                },
                canMatch: [authGuard],
            },
            {
                path: 'unauthorised',
                loadComponent: () =>
                    import('@app/shared/page-state/forbidden/forbidden.component').then(
                        m => m.ForbiddenComponent,
                    ),
                pathMatch: 'full',
                data: {
                    title: 'Forbidden',
                },
            },
        ],
    },
    {
        /* This route will be activated when no route matches in all the routing modules and
           redirects to home. The component here is never actually used. If:
           - User is not logged in - authGuard will redirect to /login
           - User is logged in - redirectToHomeGuard will redirect to home
        */
        path: '**',
        loadComponent: () => import('@app/auth/login/login.component').then(m => m.LoginComponent),
        pathMatch: 'full',
        canActivate: [authGuard, redirectToHomeGuard],
    },
];
